import React, { useState, useRef, useEffect } from 'react';
import {
  FaPen, FaTimes, FaPlus, FaUser, FaIdCard, FaSlidersH, FaBriefcase,
  FaGraduationCap, FaStar, FaLanguage, FaCertificate, FaFileAlt,
} from 'react-icons/fa';

export const palette = (isDarkMode) => (isDarkMode ? {
  card: 'bg-[#161616] border-gray-800',
  text: 'text-gray-100',
  secondaryText: 'text-gray-400',
  input: 'bg-[#202028] border-gray-700 text-gray-100 placeholder-gray-500',
  chip: 'bg-gray-800 text-gray-200',
  hover: 'hover:bg-gray-800',
  primaryBtn: 'bg-brand-purple text-white hover:bg-brand-deep shadow-purple',
  secondaryBtn: 'bg-gray-800 text-gray-100 hover:bg-gray-700',
  accent: 'text-brand-purple',
  ring: 'focus:ring-brand-purple/40',
  divider: 'border-gray-800',
} : {
  card: 'bg-white border-gray-100',
  text: 'text-ink-900',
  secondaryText: 'text-ink-500',
  input: 'bg-white border-gray-200 text-ink-900 placeholder-ink-300',
  chip: 'bg-ink-100 text-ink-700',
  hover: 'hover:bg-ink-100',
  primaryBtn: 'bg-brand-purple text-white hover:bg-brand-deep shadow-purple',
  secondaryBtn: 'bg-ink-100 text-ink-700 hover:bg-gray-200',
  accent: 'text-brand-purple',
  ring: 'focus:ring-brand-purple/40',
  divider: 'border-gray-100',
});

// Each section gets a branded icon so the page reads like the rest of the app.
export const SECTION_ICONS = {
  basic:          { Icon: FaUser,          color: '#7C3AED', bg: '#F5F3FF', darkBg: 'rgba(124,58,237,0.15)' },
  identity:       { Icon: FaIdCard,        color: '#F15A25', bg: '#FEF1EC', darkBg: 'rgba(241,90,37,0.15)' },
  preferences:    { Icon: FaSlidersH,      color: '#2563EB', bg: '#EFF6FF', darkBg: 'rgba(37,99,235,0.15)' },
  experience:     { Icon: FaBriefcase,     color: '#7C3AED', bg: '#F5F3FF', darkBg: 'rgba(124,58,237,0.15)' },
  education:      { Icon: FaGraduationCap, color: '#0EA5A4', bg: '#ECFEFF', darkBg: 'rgba(14,165,164,0.15)' },
  skills:         { Icon: FaStar,          color: '#F59E0B', bg: '#FFFBEB', darkBg: 'rgba(245,158,11,0.15)' },
  languages:      { Icon: FaLanguage,      color: '#19C37D', bg: '#F0FDF4', darkBg: 'rgba(25,195,125,0.15)' },
  certifications: { Icon: FaCertificate,   color: '#DB2777', bg: '#FDF2F8', darkBg: 'rgba(219,39,119,0.15)' },
  resume:         { Icon: FaFileAlt,       color: '#475569', bg: '#F1F5F9', darkBg: 'rgba(71,85,105,0.20)' },
};

export function SectionCard({ id, title, subtitle, optional, isDarkMode, onEdit, editing, children }) {
  const c = palette(isDarkMode);
  const meta = SECTION_ICONS[id];
  return (
    <section id={id} className={`rounded-2xl border shadow-glass p-5 sm:p-6 scroll-mt-24 transition-shadow hover:shadow-glass-lg ${c.card}`}>
      <div className={`flex items-start justify-between mb-4 pb-4 border-b ${c.divider}`}>
        <div className="flex items-start gap-3 min-w-0">
          {meta && (
            <span
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: isDarkMode ? meta.darkBg : meta.bg }}
            >
              <meta.Icon className="h-4 w-4" style={{ color: meta.color }} />
            </span>
          )}
          <div className="min-w-0">
            <h2 className={`text-base sm:text-lg font-bold font-display flex items-center gap-2 flex-wrap ${c.text}`}>
              {title}
              {optional && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.chip}`}>Optional</span>
              )}
            </h2>
            {subtitle && <p className={`text-sm mt-0.5 ${c.secondaryText}`}>{subtitle}</p>}
          </div>
        </div>
        {onEdit && !editing && (
          <button
            onClick={onEdit}
            aria-label={`Edit ${title}`}
            className={`p-2 rounded-full transition-colors shrink-0 ${c.hover} ${c.accent}`}
          >
            <FaPen className="h-4 w-4" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

export function Field({ label, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium mb-1 opacity-80">{label}</span>
      {children}
    </label>
  );
}

export function TextInput({ isDarkMode, ...props }) {
  const c = palette(isDarkMode);
  return (
    <input
      {...props}
      className={`w-full rounded-xl border px-3 py-3 text-sm focus:outline-none focus:ring-2 ${c.ring} ${c.input}`}
    />
  );
}

export function TextArea({ isDarkMode, ...props }) {
  const c = palette(isDarkMode);
  return (
    <textarea
      rows={3}
      {...props}
      className={`w-full rounded-xl border px-3 py-3 text-sm focus:outline-none focus:ring-2 ${c.ring} ${c.input}`}
    />
  );
}

export function Select({ isDarkMode, options, placeholder, ...props }) {
  const c = palette(isDarkMode);
  return (
    <select
      {...props}
      className={`w-full rounded-xl border px-3 py-3 text-sm focus:outline-none focus:ring-2 ${c.ring} ${c.input}`}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function ChipToggleGroup({ isDarkMode, options, values = [], onChange }) {
  const c = palette(isDarkMode);
  const toggle = (value) => {
    onChange(values.includes(value) ? values.filter(v => v !== value) : [...values, value]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const active = values.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={`px-3 py-2 rounded-full text-sm border transition-colors ${
              active
                ? 'bg-brand-purple text-white border-brand-purple'
                : `${c.chip} border-transparent ${c.hover}`
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function ChipInput({ isDarkMode, values = [], onChange, suggestions = [], placeholder }) {
  const c = palette(isDarkMode);
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const addValue = (raw) => {
    const value = raw.trim();
    if (!value) return;
    if (!values.some(v => v.toLowerCase() === value.toLowerCase())) {
      onChange([...values, value]);
    }
    setInput('');
  };

  const matches = suggestions
    .filter(s => s.toLowerCase().includes(input.toLowerCase()) && !values.some(v => v.toLowerCase() === s.toLowerCase()))
    .slice(0, 8);

  return (
    <div ref={wrapRef} className="relative">
      <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-2 py-2 ${c.input}`}>
        {values.map(v => (
          <span key={v} className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm ${c.chip}`}>
            {v}
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))} aria-label={`Remove ${v}`}>
              <FaTimes className="h-3 w-3 opacity-60" />
            </button>
          </span>
        ))}
        <input
          value={input}
          placeholder={values.length === 0 ? placeholder : ''}
          onChange={e => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addValue(input);
            } else if (e.key === 'Backspace' && !input && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          className="flex-1 min-w-[120px] bg-transparent px-1 py-1 text-sm focus:outline-none"
        />
      </div>
      {open && matches.length > 0 && (
        <ul className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg max-h-48 overflow-y-auto ${c.card}`}>
          {matches.map(s => (
            <li key={s}>
              <button
                type="button"
                onClick={() => { addValue(s); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm ${c.text} ${c.hover}`}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SaveRow({ isDarkMode, onSave, onCancel, saving, saveLabel = 'Save' }) {
  const c = palette(isDarkMode);
  return (
    <div className="flex gap-3 mt-4">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${c.primaryBtn}`}
      >
        {saving ? 'Saving…' : saveLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${c.secondaryBtn}`}
      >
        Cancel
      </button>
    </div>
  );
}

export function AddButton({ isDarkMode, label, onClick }) {
  const c = palette(isDarkMode);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg ${c.accent} ${c.hover}`}
    >
      <FaPlus className="h-3 w-3" /> {label}
    </button>
  );
}

export function EmptyHint({ isDarkMode, children }) {
  const c = palette(isDarkMode);
  return <p className={`text-sm italic ${c.secondaryText}`}>{children}</p>;
}
